import Vue from 'vue';
import remove from 'lodash/remove';
import dayjs from '@/utils/dayjs';

/**
 * Behaviour should be as following:
 *   1. When we first come to the paychecks page, everything is collapsed
 *   2. When we open any section, it will stay open after reload
 *   3. When 12 hours pass, all sections close
 *   4. When user signs out, all sections close
 *   5. When we create a paycheck, section for that period/subunit/work relation closes
 */

type Expansions = string[];

class PaycheckSettingsService {
  private _showPaycheckLinesOnPaychecksList: boolean;
  private _selectedYear: number;
  private _expandedSections: Expansions;
  private _expandedSectionsExpiry: dayjs.Dayjs; // use UTC

  constructor() {
    this._showPaycheckLinesOnPaychecksList = localStorage.getItem('PaycheckSettingsService.showPaycheckLinesOnPaychecksList') === 'true';
    this._selectedYear = JSON.parse(localStorage.getItem('PaycheckSettingsService.selectedYear') || 'null') || dayjs().year();
    this._expandedSectionsExpiry = dayjs.utc(JSON.parse(localStorage.getItem('PaycheckSettingsService.expandedSectionsExpiry') || '1'));
    this._expandedSections = JSON.parse(localStorage.getItem('PaycheckSettingsService.expandedSections') || '[]');
  }

  public get showPaycheckLinesOnPaychecksList(): boolean {
    return this._showPaycheckLinesOnPaychecksList;
  }

  public set showPaycheckLinesOnPaychecksList(value: boolean) {
    localStorage.setItem('PaycheckSettingsService.showPaycheckLinesOnPaychecksList', String(value));
    this._showPaycheckLinesOnPaychecksList = value;
  }

  public get selectedYear(): number {
    return this._selectedYear;
  }

  public set selectedYear(value: number) {
    localStorage.setItem('PaycheckSettingsService.selectedYear', String(value));
    this._selectedYear = value;
  }

  public get expandedSections(): Expansions {
    return this._expandedSections;
  }

  private saveExpandedSections(): void {
    localStorage.setItem('PaycheckSettingsService.expandedSections', JSON.stringify(this._expandedSections));
  }

  private resetExpandedSectionsExpiry(): void {
    this._expandedSectionsExpiry = dayjs.utc().add(12, 'hours');
    localStorage.setItem('PaycheckSettingsService.expandedSectionsExpiry', this._expandedSectionsExpiry.valueOf().toString());
  }

  public checkExpandedSectionsExpiry(): void {
    this._expandedSectionsExpiry = dayjs.utc(JSON.parse(localStorage.getItem('PaycheckSettingsService.expandedSectionsExpiry') || '1'));
    if (dayjs.utc().isAfter(this._expandedSectionsExpiry)) {
      this.clearExpandedSections();
    }
  }

  public clearExpandedSections(): void {
    this._expandedSections = [];
    this.saveExpandedSections();
    this.resetExpandedSectionsExpiry();
  }

  public expandSection(value: boolean, periodId: string, subUnitId = '', workRelationId = ''): void {
    // In timeout to stop thread blocking
    setTimeout(() => {
      const path = [ periodId, subUnitId, workRelationId ].filter(p => !!p).join('.');

      value
        ? !this.isExpandedSection(path) && this._expandedSections.push(path)
        : remove(this._expandedSections, el => el === path);

      this.saveExpandedSections();
      this.resetExpandedSectionsExpiry();
    }, 0);
  }

  public isExpandedSection(periodId: string, subUnitId = '', workRelationId = ''): boolean {
    const path = [ periodId, subUnitId, workRelationId ].filter(p => !!p).join('.');

    return this._expandedSections.includes(path);
  }
}

export default Vue.observable(new PaycheckSettingsService());
